/**
 * The matchmaker (F11).
 *
 * Pick a startup, and see the assets that could host a pilot for it, ranked,
 * each with the reasons it scored well and the blockers standing in the way.
 * PRD F11 is explicit that a match must ALWAYS show both — a bare score is a
 * black box — so every result row renders its reasons and blockers, and a
 * blocked site is shown with its blocker rather than hidden.
 *
 * The chosen startup lives in the URL (`?startupId=`), so a match is a
 * shareable link and survives a reload.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.18, PRD F11, §4.2
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import type { AssetMatch } from '@nexus/contracts';

import { Badge } from '../../components/ui/Badge';
import { Card, CardBody, CardHead } from '../../components/ui/Card';
import { Empty } from '../../components/ui/Empty';
import { Kicker } from '../../components/ui/Kicker';
import { Meter } from '../../components/ui/Meter';
import { PageHead } from '../../components/ui/PageHead';
import { Select } from '../../components/ui/Select';
import { useStartupOptions } from '../../lib/startup-options.queries';

import { useMatchmaker } from './api/matchmaker.queries';

export function MatchmakerPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const startupId = params.get('startupId');

  /* The picker's options — id/name pairs for every startup. */
  const startupsQuery = useStartupOptions();
  const options = useMemo(
    () =>
      (startupsQuery.data ?? []).map((startup) => ({
        value: startup.id,
        label: startup.name,
      })),
    [startupsQuery.data],
  );

  const { data, isPending, isError, isFetching } = useMatchmaker(
    startupId === null ? null : { startupId },
  );

  return (
    <>
      <PageHead
        kicker={t('nav.group.pipeline')}
        title={t('matchmaker.title')}
        lead={t('matchmaker.lead')}
      />

      <div className="mb-s4 max-w-[360px]">
        <label
          htmlFor="matchmaker-startup"
          className="text-navy-text mb-1 block text-sm font-semibold"
        >
          {t('matchmaker.pickStartup')}
        </label>
        <Select
          id="matchmaker-startup"
          value={startupId ?? undefined}
          onValueChange={(value) => {
            setParams({ startupId: value });
          }}
          options={options}
          placeholder={t('matchmaker.pickPlaceholder')}
          ariaLabel={t('matchmaker.pickStartup')}
        />
      </div>

      <MatchmakerBody
        hasStartup={startupId !== null}
        isPending={startupId !== null && isPending}
        isFetching={isFetching}
        isError={isError}
        matches={data?.matches ?? []}
      />
    </>
  );
}

interface BodyProps {
  hasStartup: boolean;
  isPending: boolean;
  isFetching: boolean;
  isError: boolean;
  matches: readonly AssetMatch[];
}

function MatchmakerBody({
  hasStartup,
  isPending,
  isFetching,
  isError,
  matches,
}: BodyProps): React.JSX.Element {
  const { t } = useTranslation();

  if (!hasStartup) {
    return (
      <Card>
        <Empty title={t('matchmaker.promptTitle')}>{t('matchmaker.promptBody')}</Empty>
      </Card>
    );
  }

  if (isError) {
    return (
      <p role="alert" className="text-sem-bad">
        {t('errors.generic')}
      </p>
    );
  }

  if (isPending) {
    return (
      <p className="text-body" aria-busy="true">
        {t('state.loading')}
      </p>
    );
  }

  if (matches.length === 0) {
    return (
      <Card>
        <Empty title={t('matchmaker.noMatchesTitle')}>
          {t('matchmaker.noMatchesBody')}
        </Empty>
      </Card>
    );
  }

  return (
    <div aria-busy={isFetching} className="flex flex-col gap-s3">
      {matches.map((match) => (
        <MatchCard key={match.assetId} match={match} />
      ))}
    </div>
  );
}

function MatchCard({ match }: { match: AssetMatch }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHead
        actions={
          <Meter
            value={match.score}
            max={100}
            size="sm"
            label={t('matchmaker.scoreFor', { name: match.assetName })}
            display={String(match.score)}
          />
        }
      >
        <Kicker as="h2">{match.assetName}</Kicker>
        {match.isPilotReady ? (
          <Badge tone="ok">{t('matchmaker.pilotReady')}</Badge>
        ) : (
          <Badge tone="mute">{t('matchmaker.notReady')}</Badge>
        )}
      </CardHead>
      <CardBody>
        <div className="grid gap-s3 sm:grid-cols-2">
          <div>
            <p className="font-display text-body mb-1.5 text-[11px] tracking-wide uppercase">
              {t('matchmaker.reasons')}
            </p>
            {match.reasons.length === 0 ? (
              <p className="text-gray-400 text-sm font-light">
                {t('matchmaker.noReasons')}
              </p>
            ) : (
              <ul className="text-body flex flex-col gap-1 text-sm font-light">
                {match.reasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="font-display text-body mb-1.5 text-[11px] tracking-wide uppercase">
              {t('matchmaker.blockers')}
            </p>
            {match.blockers.length === 0 ? (
              <p className="text-gray-400 text-sm font-light">
                {t('matchmaker.noBlockers')}
              </p>
            ) : (
              <ul className="text-sem-bad flex flex-col gap-1 text-sm font-light">
                {match.blockers.map((blocker) => (
                  <li key={blocker}>{blocker}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
